import React, { useState } from 'react';
import styled from 'styled-components';
import { Calendar, Clock, Package, MapPin, Phone, Truck, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const CalendarSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  background: #3b82f6;
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const MonthButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const MonthYear = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #e5e7eb;
`;

const DayHeader = styled.div`
  padding: 15px 10px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  
  &:last-child {
    border-right: none;
  }
`;

const CalendarBody = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const CalendarDay = styled.div`
  min-height: 100px;
  padding: 8px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  cursor: pointer;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: #f9fafb;
  }
  
  &.other-month {
    color: #9ca3af;
    background: #f9fafb;
  }
  
  &.today {
    background: #dbeafe;
  }
`;

const DayNumber = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  color: ${props => props.isToday ? '#1d4ed8' : props.isOtherMonth ? '#9ca3af' : '#374151'};
`;

const DeliveryItem = styled.div`
  background: ${props => {
    switch(props.status) {
      case 'scheduled': return '#fef3c7';
      case 'in-transit': return '#dbeafe';
      case 'delivered': return '#dcfce7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'scheduled': return '#d97706';
      case 'in-transit': return '#1d4ed8';
      case 'delivered': return '#15803d';
      default: return '#6b7280';
    }
  }};
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 3px;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const SchedulePanel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const PanelHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PanelTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ScheduleList = styled.div`
  padding: 20px;
`;

const ScheduleItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TimeSlot = styled.div`
  background: #f3f4f6;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ItemDetails = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`;

const ItemAddress = styled.div`
  font-size: 12px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatusBadge = styled.div`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'scheduled': return '#fef3c7';
      case 'in-transit': return '#dbeafe';
      case 'delivered': return '#dcfce7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'scheduled': return '#d97706';
      case 'in-transit': return '#1d4ed8';
      case 'delivered': return '#15803d';
      default: return '#6b7280';
    }
  }};
`;

const NotificationSettings = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const NotificationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
  margin-bottom: 8px;
`;

const NotificationText = styled.div`
  font-size: 12px;
  color: #075985;
`;

function DeliverySchedule() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // 더미 배송 예정 데이터
  const deliverySchedule = {
    '2024-02-16': [
      {
        id: 1,
        time: '09:00-12:00',
        trackingNumber: 'TK123456789',
        productName: '프리미엄 무선 헤드폰',
        status: 'scheduled',
        address: '서울시 강남구 테헤란로 123',
        courier: 'CJ대한통운',
        recipient: '김철수'
      },
      {
        id: 2,
        time: '14:00-18:00',
        trackingNumber: 'TK987654321',
        productName: '스마트워치 시리즈 9',
        status: 'in-transit',
        address: '서울시 서초구 강남대로 456',
        courier: '한진택배',
        recipient: '이영희'
      }
    ],
    '2024-02-17': [
      {
        id: 3,
        time: '10:00-14:00',
        trackingNumber: 'TK456789123',
        productName: '블루투스 이어폰',
        status: 'scheduled',
        address: '경기도 성남시 분당구',
        courier: '로젠택배',
        recipient: '박민수'
      }
    ],
    '2024-02-15': [
      {
        id: 4,
        time: '15:30',
        trackingNumber: 'TK789123456',
        productName: '클래식 레더 자켓',
        status: 'delivered',
        address: '부산시 해운대구',
        courier: 'CJ대한통운',
        recipient: '최지현'
      }
    ]
  };

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 달력 생성
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysFromPrevMonth = firstDayOfMonth === 0 ? 0 : firstDayOfMonth;

  const calendarDays = [];
  
  // 이전 달 날짜들
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    calendarDays.push({
      date,
      day: date.getDate(),
      isCurrentMonth: false,
      isToday: false,
      deliveries: []
    });
  }

  // 현재 달 날짜들
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateKey = date.toISOString().split('T')[0];
    const isToday = date.toDateString() === today.toDateString();
    
    calendarDays.push({
      date,
      day,
      isCurrentMonth: true,
      isToday,
      deliveries: deliverySchedule[dateKey] || []
    });
  }

  // 다음 달 날짜들 (42일로 맞추기)
  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(year, month + 1, day);
    calendarDays.push({
      date,
      day,
      isCurrentMonth: false,
      isToday: false,
      deliveries: []
    });
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const getSelectedDateDeliveries = () => {
    const dateKey = selectedDate.toISOString().split('T')[0];
    return deliverySchedule[dateKey] || [];
  };

  const formatSelectedDate = () => {
    return selectedDate.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const dayHeaders = ['일', '월', '화', '수', '목', '금', '토'];
  const monthNames = [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ];

  return (
    <ContentLayout 
      title="배송 예정"
      subtitle="예정된 배송 일정을 달력으로 확인하고 관리할 수 있습니다"
    >
      <Container>
        <NotificationSettings>
          <NotificationTitle>
            <Bell size={16} />
            배송 알림 설정
          </NotificationTitle>
          <NotificationText>
            SMS 및 푸시 알림으로 배송 예정일을 미리 알려드립니다. 
            알림 설정은 마이페이지에서 변경하실 수 있습니다.
          </NotificationText>
        </NotificationSettings>

        <CalendarSection>
          <CalendarHeader>
            <MonthNavigation>
              <MonthButton onClick={() => navigateMonth(-1)}>
                <ChevronLeft size={20} />
              </MonthButton>
              <MonthYear>
                {year}년 {monthNames[month]}
              </MonthYear>
              <MonthButton onClick={() => navigateMonth(1)}>
                <ChevronRight size={20} />
              </MonthButton>
            </MonthNavigation>
          </CalendarHeader>

          <CalendarGrid>
            {dayHeaders.map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
          </CalendarGrid>

          <CalendarBody>
            {calendarDays.map((day, index) => (
              <CalendarDay 
                key={index}
                className={`${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''}`}
                onClick={() => handleDateClick(day.date)}
              >
                <DayNumber 
                  isToday={day.isToday}
                  isOtherMonth={!day.isCurrentMonth}
                >
                  {day.day}
                </DayNumber>
                {day.deliveries.slice(0, 3).map((delivery, idx) => (
                  <DeliveryItem key={idx} status={delivery.status}>
                    {delivery.time} {delivery.productName}
                  </DeliveryItem>
                ))}
                {day.deliveries.length > 3 && (
                  <DeliveryItem>
                    +{day.deliveries.length - 3}개 더
                  </DeliveryItem>
                )}
              </CalendarDay>
            ))}
          </CalendarBody>
        </CalendarSection>

        <SchedulePanel>
          <PanelHeader>
            <Calendar size={20} />
            <PanelTitle>{formatSelectedDate()} 배송 예정</PanelTitle>
          </PanelHeader>
          
          <ScheduleList>
            {getSelectedDateDeliveries().length > 0 ? (
              getSelectedDateDeliveries().map(delivery => (
                <ScheduleItem key={delivery.id}>
                  <TimeSlot>{delivery.time}</TimeSlot>
                  <ItemInfo>
                    <ItemTitle>{delivery.productName}</ItemTitle>
                    <ItemDetails>
                      {delivery.trackingNumber} · {delivery.courier} · 수령인: {delivery.recipient}
                    </ItemDetails>
                    <ItemAddress>
                      <MapPin size={12} />
                      {delivery.address}
                    </ItemAddress>
                  </ItemInfo>
                  <StatusBadge status={delivery.status}>
                    {delivery.status === 'delivered' ? '배송완료' :
                     delivery.status === 'in-transit' ? '배송중' : '예정'}
                  </StatusBadge>
                </ScheduleItem>
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#6b7280'
              }}>
                <Package size={40} style={{margin: '0 auto 15px', display: 'block'}} />
                <div>선택한 날짜에 예정된 배송이 없습니다</div>
              </div>
            )}
          </ScheduleList>
        </SchedulePanel>
      </Container>
    </ContentLayout>
  );
}

export default DeliverySchedule;